import {
  Autocomplete,
  FileInput,
  GeocoderService,
  OtherReportIcon,
  Place,
  Report,
  ReportService,
  reportTypeOthers,
  reportTypesCategories,
  reportTypesCategoriesMap,
  reportTypesChildren,
  TFile,
  TReportParentsCode,
  TReportTypeCode,
  TReportTypesCategory,
  useGeoveloMap,
} from '@geovelo-frontends/commons';
import { ChevronLeft } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  SvgIconProps,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormikHelpers, useFormik } from 'formik';
import { Marker } from 'maplibre-gl';
import { useSnackbar } from 'notistack';
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { AppContext } from '../context';

import Button from './button';
import Dialog from './dialog';

interface IValues {
  type: TReportTypeCode | null;
  description: string;
}

type TProps = Omit<DialogProps, 'onClose'> & { onClose: (report?: Report) => void };

const mapId = 'new-report-map';

function NewReportDialog({ onClose, ...props }: TProps): React.JSX.Element {
  const [file, setFile] = useState<TFile | undefined>();
  const [selectedCategoryKey, selectCategoryKey] = useState<TReportTypesCategory | null>(null);
  const [selectedSubCategoryKey, selectSubCategoryKey] = useState<TReportParentsCode | null>(null);
  const [selectedPlace, selectPlace] = useState<Place | null>(null);
  const [placeError, setPlaceError] = useState(false);
  const {
    map: { current: mainMap },
    report: { typesMap: reportTypesMap },
  } = useContext(AppContext);
  const markerRef = useRef<Marker>(null);
  const { t } = useTranslation();
  const { palette, transitions } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const {
    isSubmitting,
    values,
    errors,
    isValid,
    setFieldValue,
    resetForm,
    handleChange,
    handleSubmit,
  } = useFormik<IValues>({
    initialValues: { type: null, description: '' },
    validationSchema: Yup.object().shape({
      type: Yup.string().required(),
      description: Yup.string().min(10).required(),
    }),
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit,
  });
  const {
    map,
    init: initMap,
    destroy: destroyMap,
  } = useGeoveloMap({
    smallDevice: true,
    baseLayer: 'geovelo',
  });

  useEffect(() => {
    if (props.open && mainMap) {
      resetForm({ values: { type: null, description: '' } });
      selectCategoryKey(null);
      selectSubCategoryKey(null);
      setFile(undefined);

      const center = mainMap.getCenter();

      setTimeout(() => {
        initMap({
          container: mapId,
          customZoomControls: true,
          baseLayersControl: true,
          center,
          zoom: 15,
        });
      }, transitions.duration.enteringScreen);
    }

    return () => destroyMap();
  }, [props.open]);

  useEffect(() => {
    if (mainMap && map) {
      const center = mainMap.getCenter();

      markerRef.current = new Marker({ color: palette.secondary.main, draggable: true })
        .setLngLat(center)
        .addTo(map);

      markerRef.current.on('dragend', ({ target }: { target: Marker }) => {
        map.flyTo({ center: target.getLngLat(), zoom: Math.max(15, map.getZoom()) });
        selectPlace(
          new Place(undefined, {
            type: 'Point',
            coordinates: target.getLngLat().toArray(),
          }),
        );
      });

      selectPlace(new Place(undefined, { type: 'Point', coordinates: center.toArray() }));
    }
  }, [map]);

  useEffect(() => {
    if (isSubmitting && markerRef.current) {
      markerRef.current.setDraggable(!isSubmitting);
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (selectedPlace && !selectedPlace.addressDetail) {
      try {
        GeocoderService.reverseGeocode(undefined, selectedPlace.point, (place) => {
          selectPlace(place);
        });
      } catch (err) {
        console.error(err);
      }
    }
  }, [selectedPlace]);

  async function onSubmit(
    { type: typeCode, description }: IValues,
    { setSubmitting }: FormikHelpers<IValues>,
  ) {
    if (!typeCode) return;

    const type = reportTypesMap?.[typeCode];
    if (!type || !markerRef.current) return;

    if (!selectedPlace) {
      setPlaceError(true);
      return;
    }

    setSubmitting(true);
    setPlaceError(false);
    markerRef.current.setDraggable(false);

    try {
      const { lat, lng } = markerRef.current.getLngLat();
      const point: GeoJSON.Point = {
        type: 'Point',
        coordinates: [lng, lat],
      };

      const place = await GeocoderService.reverseGeocode(undefined, point);
      const report = await ReportService.addReport({
        type: type.id,
        place,
        description,
        photo: file instanceof File ? file : undefined,
      });

      onClose(report);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t('geovelo.new_report_dialog.server_error'));
    }

    setSubmitting(false);
    markerRef.current.setDraggable(true);
  }

  const selectedCategory = selectedCategoryKey
    ? reportTypesCategoriesMap[selectedCategoryKey]
    : null;
  const selectedSubCategory =
    (selectedSubCategoryKey && reportTypesMap?.[selectedSubCategoryKey]) || null;
  const selectedSubCategoryChildren =
    (selectedSubCategoryKey && reportTypesChildren[selectedSubCategoryKey].children) || [];

  return (
    <Dialog fullWidth maxWidth="sm" scroll="body" {...props}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('geovelo.new_report_dialog.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Autocomplete
            defaultValue={selectedPlace}
            disableClearable={true}
            disabled={!values.type}
            error={placeError}
            onSelect={(place) => {
              selectPlace(place);
              if (!place) return;
              const position = { lng: place.point.coordinates[0], lat: place.point.coordinates[1] };
              map?.flyTo({ center: position, zoom: Math.max(15, map.getZoom()) });
              markerRef.current?.setLngLat(position);
            }}
            size="small"
          />
          <Box height={200} id={mapId} marginX={-3} position="relative" />
          <Box display="flex" flexDirection="column" gap={2}>
            <Box alignItems="center" display="flex" gap={2} height={30}>
              {selectedSubCategory && selectedSubCategoryChildren.length > 0 ? (
                <IconButton
                  onClick={() => {
                    selectSubCategoryKey(null);
                    setFieldValue('type', null);
                  }}
                  size="small"
                >
                  <ChevronLeft color="primary" fontSize="small" />
                </IconButton>
              ) : (
                selectedCategory && (
                  <IconButton
                    onClick={() => {
                      selectCategoryKey(null);
                      setFieldValue('type', null);
                    }}
                    size="small"
                  >
                    <ChevronLeft color="primary" fontSize="small" />
                  </IconButton>
                )
              )}
              <Typography fontWeight={700} variant="body1">
                {selectedSubCategory && selectedSubCategoryChildren.length > 0
                  ? t(selectedSubCategory.titleKey)
                  : selectedCategory
                    ? t(selectedCategory.titleKey)
                    : t('geovelo.new_report_dialog.type')}
              </Typography>
            </Box>
            <Box
              display="flex"
              flexWrap="wrap"
              gap={2}
              sx={{ containerType: 'inline-size' }}
              width="100%"
            >
              {selectedSubCategoryKey && selectedSubCategoryChildren.length > 0
                ? reportTypesChildren[selectedSubCategoryKey].children.map((key) => {
                    if (reportTypeOthers.includes(key)) return <Fragment key={key} />;
                    const type = reportTypesMap?.[key as TReportTypeCode];
                    if (!type) return <Fragment key={key} />;

                    const active = values.type === key;

                    return (
                      <Item
                        circleIcon
                        active={active}
                        item={type}
                        key={key}
                        onClick={() => setFieldValue('type', key)}
                      />
                    );
                  })
                : selectedCategory
                  ? selectedCategory.children.map((key) => {
                      const type =
                        key === 'dangerous'
                          ? {
                              Icon: OtherReportIcon,
                              titleKey: 'commons.facilities.other',
                              color: '#da3a87',
                            }
                          : reportTypesMap?.[key];
                      if (!type) return <Fragment key={key} />;

                      const active = values.type === key;

                      return (
                        <Item
                          active={active}
                          item={type}
                          key={key}
                          onClick={() => {
                            selectSubCategoryKey(key);
                            if (reportTypesChildren[key].children.length === 0)
                              setFieldValue('type', key);
                            else setFieldValue('type', null);
                          }}
                        />
                      );
                    })
                  : reportTypesCategories.map((key) => (
                      <Item
                        item={reportTypesCategoriesMap[key]}
                        key={key}
                        onClick={() => selectCategoryKey(key)}
                      />
                    ))}
            </Box>
          </Box>
          <TextField
            fullWidth
            multiline
            required
            disabled={isSubmitting}
            error={Boolean(errors.description)}
            id="description"
            label={t('geovelo.new_report_dialog.description_label')}
            margin="none"
            name="description"
            onChange={handleChange}
            rows={1}
            size="small"
            slotProps={{ htmlInput: { minLength: 10 }, inputLabel: { shrink: true } }}
            value={values.description}
            variant="outlined"
          />
          <FileInput
            disabled={isSubmitting}
            file={file}
            onChange={setFile}
            size="small"
            type="image"
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} onClick={() => onClose()} type="reset">
            {t('commons.actions.cancel')}
          </Button>
          <Button
            color="primary"
            disabled={isSubmitting || !isValid}
            type="submit"
            variant="contained"
          >
            {t('commons.actions.contribute')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function Item({
  active,
  circleIcon,
  item: { Icon, titleKey, color },
  onClick,
}: {
  active?: boolean;
  circleIcon?: boolean;
  item: {
    color?: string;
    Icon: (props: SvgIconProps) => React.JSX.Element;
    titleKey: string;
  };
  onClick: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Box
      alignItems="center"
      border={`1px solid ${(active && (circleIcon ? '#000' : color)) || '#ddd'}`}
      borderRadius={2}
      component={ButtonBase}
      display="flex"
      flexDirection="column"
      gap={1}
      justifyContent="flex-start"
      onClick={onClick}
      padding={2}
      width={{
        '@': 'calc((100% - 16px) / 2)',
        '@500': 'calc((100% - 48px) / 4)',
      }}
    >
      {circleIcon ? (
        <Box
          alignItems="center"
          border="1px solid #000"
          borderRadius="27px"
          display="flex"
          height={54}
          justifyContent="center"
          width={54}
        >
          <Icon sx={{ height: 40, width: 40 }} />
        </Box>
      ) : (
        <Icon sx={{ height: 54, width: 54 }} />
      )}
      <Typography align="center" variant="body2">
        {t(titleKey)}
      </Typography>
    </Box>
  );
}

export default NewReportDialog;
